import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MapPin, Edit, Trash2, Loader2, CheckCircle, XCircle } from "lucide-react";

const WORKING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIME_SLOTS = ["09:00-12:00", "12:00-15:00", "15:00-18:00", "18:00-21:00"];

// Extracted LocationForm component to prevent cursor jumping
const LocationForm = ({ 
  onSubmit, 
  submitText, 
  formData, 
  setFormData, 
  pincodeLoading,
  returnPincodeLoading,
  lookupPincode 
}) => (
  <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="facility_name">Facility Name *</Label>
        <Input
          id="facility_name"
          value={formData.facility_name}
          onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person_name">Contact Person Name *</Label>
        <Input
          id="contact_person_name"
          value={formData.contact_person_name}
          onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="contact_phone">Contact Phone *</Label>
        <Input
          id="contact_phone"
          value={formData.contact_phone}
          onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          maxLength={10}
          inputMode="numeric"
          placeholder="10-digit number"
          required
        />
        {formData.contact_phone && formData.contact_phone.length !== 10 && (
          <p className="text-xs text-red-600">Enter a valid 10-digit phone number (required for the Delhivery manifest).</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_email">Contact Email *</Label>
        <Input
          id="contact_email"
          type="email"
          value={formData.contact_email}
          onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="address_line">Address Line *</Label>
      <Input
        id="address_line"
        value={formData.address_line}
        onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
        required
      />
    </div>

    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label htmlFor="pincode">Pincode *</Label>
        <div className="flex gap-2">
          <Input
            id="pincode"
            value={formData.pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              setFormData({ ...formData, pincode: value });
              if (value.length === 6) lookupPincode(value);
            }}
            maxLength={6}
            inputMode="numeric"
            required
          />
          {pincodeLoading && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
        </div>
        {formData.pincode && formData.pincode.length !== 6 && (
          <p className="text-xs text-red-600">Pincode must be 6 digits.</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="city">City *</Label>
        <Input
          id="city"
          value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="state">State *</Label>
        <Input
          id="state"
          value={formData.state}
          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="space-y-4 p-4 border rounded-lg bg-slate-50">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="has_return_address"
          checked={formData.has_return_address}
          onCheckedChange={(checked) => setFormData({ ...formData, has_return_address: checked })}
        />
        <Label htmlFor="has_return_address" className="text-sm font-medium">
          Return address is different
        </Label>
      </div>

      {formData.has_return_address && (
        <div className="space-y-4 pl-6 border-l-2 border-slate-300">
          <div className="space-y-2">
            <Label htmlFor="return_address_line">Return Address Line *</Label>
            <Input
              id="return_address_line"
              value={formData.return_address?.address_line || ""}
              onChange={(e) => setFormData({
                ...formData,
                return_address: { ...formData.return_address, address_line: e.target.value }
              })}
              required={formData.has_return_address}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="return_pincode">Pincode *</Label>
              <div className="flex gap-2">
                <Input
                  id="return_pincode"
                  value={formData.return_address?.pincode || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({
                      ...formData,
                      return_address: { ...formData.return_address, pincode: value }
                    });
                    if (value.length === 6) lookupPincode(value, true);
                  }}
                  maxLength={6}
                  required={formData.has_return_address}
                />
                {returnPincodeLoading && <Loader2 className="w-4 h-4 animate-spin mt-2" />}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="return_city">City *</Label>
              <Input
                id="return_city"
                value={formData.return_address?.city || ""}
                onChange={(e) => setFormData({
                  ...formData,
                  return_address: { ...formData.return_address, city: e.target.value }
                })}
                required={formData.has_return_address}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="return_state">State *</Label>
              <Input
                id="return_state"
                value={formData.return_address?.state || ""}
                onChange={(e) => setFormData({
                  ...formData,
                  return_address: { ...formData.return_address, state: e.target.value }
                })}
                required={formData.has_return_address}
              />
            </div>
          </div>
        </div>
      )}
    </div>

    <div className="space-y-4">
      <Label>Working Days & Hours *</Label>
      <div className="grid grid-cols-2 gap-2">
        {WORKING_DAYS.map((day) => (
          <div key={day} className="flex items-center space-x-2">
            <Checkbox
              id={day}
              checked={formData.working_days?.includes(day)}
              onCheckedChange={(checked) => {
                const currentDays = formData.working_days || [];
                const newDays = checked
                  ? [...currentDays, day]
                  : currentDays.filter((d) => d !== day);
                setFormData({ ...formData, working_days: newDays });
              }}
            />
            <Label htmlFor={day} className="text-sm">{day}</Label>
          </div>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>Operating Hours *</Label>
      <Select
        value={formData.operating_hours}
        onValueChange={(value) => setFormData({ ...formData, operating_hours: value })}
        required
      >
        <SelectTrigger>
          <SelectValue placeholder="Select time slot" />
        </SelectTrigger>
        <SelectContent>
          {TIME_SLOTS.map((slot) => (
            <SelectItem key={slot} value={slot}>
              {slot}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
      {submitText}
    </Button>
  </form>
);

const PickupLocations = ({ user, onLogout }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [returnPincodeLoading, setReturnPincodeLoading] = useState(false);

  const [formData, setFormData] = useState({
    seller_id: user.seller_id || "",
    facility_name: "",
    contact_person_name: "",
    pickup_contact: "",
    email: "",
    address_line: "",
    pincode: "",
    city: "",
    state: "",
    default_pickup_slot: "",
    working_days: [],
    return_address_same: true,
    return_address: {
      address_line: "",
      pincode: "",
      city: "",
      state: "",
    },
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async (query = "") => {
    try {
      const url = query ? `${API}/pickup-locations/?q=${encodeURIComponent(query)}` : `${API}/pickup-locations/`;
      const response = await axios.get(url);
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch pickup locations");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    fetchLocations(query);
  };

  const lookupPincode = async (pincode, isReturn = false) => {
    if (pincode.length !== 6) return;

    if (isReturn) {
      setReturnPincodeLoading(true);
    } else {
      setPincodeLoading(true);
    }

    try {
      const response = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      if (isReturn) {
        setFormData((prev) => ({
          ...prev,
          return_address: {
            ...prev.return_address,
            pincode: pincode,
            city: response.data.city,
            state: response.data.state,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          pincode: pincode,
          city: response.data.city,
          state: response.data.state,
        }));
      }
      toast.success("Pincode verified!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pincode");
    } finally {
      if (isReturn) {
        setReturnPincodeLoading(false);
      } else {
        setPincodeLoading(false);
      }
    }
  };

  const handleWorkingDayToggle = (day) => {
    const currentDays = formData.working_days;
    if (currentDays.includes(day)) {
      setFormData({ ...formData, working_days: currentDays.filter((d) => d !== day) });
    } else {
      setFormData({ ...formData, working_days: [...currentDays, day] });
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.contact_phone || "")) { toast.error("Enter a valid 10-digit contact phone."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      // pickup_contact is the field the Delhivery manifest reads; keep it in sync.
      const payload = { ...formData, pickup_contact: formData.contact_phone };
      if (formData.return_address_same) {
        payload.return_address = null;
      }
      await axios.post(`${API}/pickup-locations/`, payload);
      toast.success("Pickup location created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.contact_phone || "")) { toast.error("Enter a valid 10-digit contact phone."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      const payload = { ...formData, pickup_contact: formData.contact_phone };
      delete payload.seller_id;
      if (formData.return_address_same) {
        payload.return_address = null;
      }
      await axios.put(`${API}/pickup-locations/${selectedLocation.id}`, payload);
      toast.success("Pickup location updated successfully!");
      setShowEditDialog(false);
      setSelectedLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleToggleStatus = async (locationId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this pickup location?`)) return;

    try {
      const response = await axios.patch(`${API}/pickup-locations/${locationId}/toggle-status`);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const resetForm = () => {
    setFormData({
      seller_id: user.seller_id || "",
      facility_name: "",
      contact_person_name: "",
      pickup_contact: "",
      email: "",
      address_line: "",
      pincode: "",
      city: "",
      state: "",
      default_pickup_slot: "",
      working_days: [],
      return_address_same: true,
      return_address: {
        address_line: "",
        pincode: "",
        city: "",
        state: "",
      },
    });
  };

  const formatErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || JSON.stringify(err)).join(", ");
    }
    return detail || "An error occurred";
  };

  const openEditDialog = (location) => {
    setFormData({
      facility_name: location.facility_name,
      contact_person_name: location.contact_person_name || "",
      contact_phone: location.contact_phone || location.pickup_contact || "",
      contact_email: location.contact_email || "",
      address_line: location.address_line,
      pincode: location.pincode,
      city: location.city,
      state: location.state,
      operating_hours: location.operating_hours || "",
      working_days: location.working_days || [],
      has_return_address: location.has_return_address || false,
      return_address: location.return_address || {
        address_line: "",
        pincode: "",
        city: "",
        state: "",
      },
    });
    setSelectedLocation(location);
    setShowEditDialog(true);
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Pickup Locations</h1>
            <p className="text-slate-600">Manage your pickup locations</p>
          </div>
          <div className="flex gap-4 items-center">
            <SearchInput 
              onSearch={handleSearch}
              placeholder="Search locations..."
            />
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-pickup-location-button" style={{backgroundColor: '#00A699'}} onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pickup Location
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Add Pickup Location</DialogTitle>
                  <DialogDescription>Create a new pickup location for your organization</DialogDescription>
                </DialogHeader>
                <LocationForm 
                  onSubmit={handleCreateLocation} 
                  submitText="Create Location"
                  formData={formData}
                  setFormData={setFormData}
                  pincodeLoading={pincodeLoading}
                  returnPincodeLoading={returnPincodeLoading}
                  lookupPincode={lookupPincode}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-full"></div>
                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : locations.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <MapPin className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No pickup locations yet</h3>
              <p className="text-slate-500 mb-6">Add your first pickup location to get started</p>
              <Button data-testid="empty-create-pickup-button" style={{backgroundColor: '#00A699'}} onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Pickup Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Facility</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Address</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Working Days</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Slot</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {locations.map((location) => (
                    <tr key={location.id} data-testid={`pickup-location-row-${location.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-mono text-blue-600 font-medium">{location.location_id}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium text-slate-900">{location.facility_name}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div>
                          <p className="text-sm text-slate-900">{location.contact_person_name}</p>
                          <p className="text-xs text-slate-500">{location.contact_phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="max-w-xs">
                          <p className="text-sm text-slate-700 truncate">{location.address_line}</p>
                          <p className="text-xs text-slate-500">{location.city}, {location.state} - {location.pincode}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <span className="text-sm text-slate-700">{location.working_days?.length || 0} days</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <span className="text-sm text-slate-700">{location.operating_hours}</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <Badge className={location.is_active ? "bg-green-100 text-green-800 border-0" : "bg-red-100 text-red-800 border-0"}>
                          {location.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-pickup-button-${location.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(location)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`toggle-pickup-button-${location.id}`}
                            size="sm"
                            variant="ghost"
                            className={location.is_active ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleStatus(location.id, location.is_active)}
                            title={location.is_active ? "Disable" : "Enable"}
                          >
                            {location.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Pickup Location</DialogTitle>
            <DialogDescription>Update pickup location details</DialogDescription>
          </DialogHeader>
          <LocationForm 
            onSubmit={handleUpdateLocation} 
            submitText="Update Location"
            formData={formData}
            setFormData={setFormData}
            pincodeLoading={pincodeLoading}
            returnPincodeLoading={returnPincodeLoading}
            lookupPincode={lookupPincode}
          />
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default PickupLocations;
